import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/utils";

import type { VariantProps } from "class-variance-authority";

const buttonVariants = cva(
  // base
  "inline-flex items-center justify-center gap-2 rounded-[25px] font-[500] tracking-[0.32px] whitespace-nowrap no-underline ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
  {
    variants: {
      variant: {
        primary:
          "[background-image:var(--pm-gradient-gold-2)] text-white! [&_*]:text-white! hover:opacity-90",
        secondary:
          "[background-image:var(--pm-gradient-gold-1)] text-white! [&_*]:text-white! ring-1 ring-black/10 hover:opacity-90",
        ghost: "bg-transparent text-garnet hover:bg-white",
        outline: "bg-transparent text-garnet hover:bg-white ring ring-garnet",
        link: "underline text-[#511A10] font-[700] text-[14px] leading-[135%] bg-transparent px-0 py-0 hover:opacity-80",
      },
      size: {
        xs: "h-9  px-4 text-[13px] leading-[36px]",
        sm: "h-10 px-5 text-[14px] leading-[40px]",
        md: "h-11 px-6 text-[16px] leading-[44px]",
        lg: "h-12 px-7 text-[16px] leading-[48px]",
        icon: "h-9 w-9 p-0 rounded-full leading-none [&>svg]:h-4 [&>svg]:w-4",
      },
      disabledState: {
        enabled: "",
        disabled: "opacity-50 cursor-not-allowed",
      },
    },
    compoundVariants: [
      // link shouldn't pick up height/padding from sizes
      { variant: "link", size: "xs", class: "" },
      { variant: "link", size: "sm", class: "" },
      { variant: "link", size: "md", class: "" },
      { variant: "link", size: "lg", class: "" },
      { variant: "link", size: "icon", class: "rounded-none p-0" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      disabledState: "enabled",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  disabledState?: "enabled" | "disabled";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      disabledState,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const computedDisabledState =
      disabled || props["aria-disabled"] ? "disabled" : disabledState;

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            disabledState: computedDisabledState,
          }),
          className,
        )}
        ref={ref}
        disabled={disabled}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export default Button;
export { buttonVariants };
